/**
 * Export Mesh Modal — lets the user choose format and settings for
 * exporting the selected object as OBJ or GLB.
 */

import { useCallback, useState } from "react";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";
import * as THREE from "three/webgpu";
import { useIsTrial } from "../app/authStore.ts";
import {
  buildSceneObjectWorldNameMap,
  getCollectionForObject,
  worldTransformForSceneObject,
} from "../app/collectionGraph.ts";
import { triggerDownload } from "../app/fileSystemAccess.ts";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import { resolveAutoSmoothAngle } from "../modeling/meshOps.ts";
import type { SceneMaterial, SceneObject } from "../modeling/types.ts";
import { DEFAULT_MATERIAL } from "../modeling/types.ts";
import { computeWeldedNormals } from "../viewport/computeWeldedNormals.ts";
import {
  buildModifierEvaluationRequest,
  evaluateModifierStackAsync,
} from "../viewport/modifierApply.ts";
import { sceneObjectGeometryCacheKey } from "../viewport/modifiers/cacheKeys.ts";
import {
  buildSceneObjectNameMap,
  mergeModifierStack,
  modifierStackNeedsOwnerWorldTransform,
} from "../viewport/modifiers/evaluation.ts";
import {
  buildRenderModifierGeometry,
  finalizePerCorner,
} from "../viewport/modifiers/renderGeometry.ts";
import { Button } from "./Button.tsx";
import { createImperativeDialog } from "./imperativeDialog.tsx";
import { ModalActions, ModalShell } from "./ModalShell.tsx";
import {
  buildPairedExportStem,
  collectUsedMaterialIndices,
  filterVisibleMeshExportSourceKeys,
  sanitizeOBJName,
  serializeMTL,
  serializeOBJBlock,
} from "./meshExportUtils.ts";
import { PANEL_INPUT_STYLE } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_GOLD,
  COLOR_TEXT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_TINY,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Imperative opener
// ---------------------------------------------------------------------------

const exportMeshDialog = createImperativeDialog<Record<string, never>, void>({
  render: (_args, close) => <ExportMeshModalBody close={() => close()} />,
});

export function openExportMeshModal(): void {
  exportMeshDialog.show({});
}

export const ExportMeshModal = exportMeshDialog.Host;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ExportFormat = "obj" | "glb";
type UpAxis = "Y" | "Z";
type ExportState = ReturnType<typeof useStore.getState>;

interface ExportMaterialSlot {
  key: string;
  label: string;
  material: SceneMaterial;
}

interface OBJMaterialRegistry {
  byKey: Map<string, string>;
  entries: { name: string; material: SceneMaterial }[];
  usedNames: Set<string>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the first exportable mesh for the given store key.
 *
 * Lookup strategy:
 * 1. Direct name match on a Mesh (works for blend objects and scene primitives).
 * 2. Find any Object3D named `key`, then pick its first visible Mesh descendant
 *    (works for mannequins whose wrapper group is named with the store key but
 *    the internal SkinnedMesh has a different name like "Human").
 */
function findMeshByStoreKey(
  scene: THREE.Scene,
  key: string,
): THREE.Mesh | null {
  // Strategy 1: direct mesh name match
  let directMatch: THREE.Mesh | null = null;
  scene.traverse((obj) => {
    if (directMatch) return;
    if (!(obj instanceof THREE.Mesh)) return;
    if (obj.userData?.excludeFromExport) return;
    if (obj.name === key) directMatch = obj;
  });
  if (directMatch) return directMatch;

  // Strategy 2: find a named group/object, then its first mesh descendant
  const namedObj = scene.getObjectByName(key);
  if (!namedObj) return null;

  let descendant: THREE.Mesh | null = null;
  namedObj.traverse((child) => {
    if (descendant) return;
    if (!(child instanceof THREE.Mesh)) return;
    if (child.userData?.excludeFromExport) return;
    descendant = child;
  });
  return descendant;
}

function createOBJMaterialRegistry(): OBJMaterialRegistry {
  return {
    byKey: new Map(),
    entries: [],
    usedNames: new Set(),
  };
}

function allocateOBJMaterialName(
  registry: OBJMaterialRegistry,
  preferred: string,
): string {
  const base = sanitizeOBJName(preferred, "Material");
  if (!registry.usedNames.has(base)) {
    registry.usedNames.add(base);
    return base;
  }
  for (let n = 2; ; n++) {
    const candidate = `${base}_${n}`;
    if (!registry.usedNames.has(candidate)) {
      registry.usedNames.add(candidate);
      return candidate;
    }
  }
}

function resolveExportMaterialSlots(
  state: ExportState,
  sceneObjectsByName: ReadonlyMap<string, SceneObject>,
  objDef: SceneObject | undefined,
): ExportMaterialSlot[] {
  if (!objDef) return [];

  const mergedMods = mergeModifierStack(
    [],
    state.modifierOverrides.get(objDef.id) ?? [],
  );
  const instanceMaterialMod = mergedMods.find(
    (modifier) =>
      modifier.kind === "instance" &&
      modifier.enabled &&
      modifier.useSourceMaterial &&
      !!modifier.sourceObjectName,
  );

  let slotOwner = objDef;
  if (
    instanceMaterialMod?.kind === "instance" &&
    instanceMaterialMod.sourceObjectName
  ) {
    slotOwner =
      sceneObjectsByName.get(instanceMaterialMod.sourceObjectName) ?? objDef;
  }

  return slotOwner.materialSlots.map((slot, idx) => {
    const block = state.materials.get(slot.materialId);
    if (block) {
      return { key: block.id, label: block.name, material: block.properties };
    }
    return {
      key: slot.materialId,
      label: `${slotOwner.name} Slot ${idx + 1}`,
      material: { ...DEFAULT_MATERIAL },
    };
  });
}

function ensureOBJMaterialName(
  registry: OBJMaterialRegistry,
  slot: ExportMaterialSlot,
): string {
  const existing = registry.byKey.get(slot.key);
  if (existing) return existing;
  const name = allocateOBJMaterialName(registry, slot.label);
  registry.byKey.set(slot.key, name);
  registry.entries.push({ name, material: slot.material });
  return name;
}

function buildOBJMaterialNamesByIndex(
  geo: THREE.BufferGeometry,
  slots: ExportMaterialSlot[],
  registry: OBJMaterialRegistry,
): Array<string | undefined> {
  if (slots.length === 0) return [];
  const usedIndices = collectUsedMaterialIndices(geo);
  let maxUsedIndex = 0;
  for (const idx of usedIndices) if (idx > maxUsedIndex) maxUsedIndex = idx;
  const count = Math.max(slots.length, maxUsedIndex + 1);
  const out = new Array<string | undefined>(count);
  const fallbackSlot = slots[0];
  for (const idx of usedIndices) {
    const slot = slots[idx] ?? fallbackSlot;
    if (!slot) continue;
    out[idx] = ensureOBJMaterialName(registry, slot);
  }
  if (!out[0] && fallbackSlot)
    out[0] = ensureOBJMaterialName(registry, fallbackSlot);
  return out;
}

function buildGLBExportMaterial(
  geo: THREE.BufferGeometry,
  slots: ExportMaterialSlot[],
): THREE.Material | THREE.Material[] | null {
  if (slots.length === 0) return null;

  const usedIndices = collectUsedMaterialIndices(geo);
  let requiresArray = false;
  let maxUsedIndex = 0;
  for (const idx of usedIndices) {
    if (idx !== 0) requiresArray = true;
    if (idx > maxUsedIndex) maxUsedIndex = idx;
  }
  if (usedIndices.size > 1) requiresArray = true;

  if (!requiresArray) return buildExportMaterial(slots[0].material);

  const count = Math.max(slots.length, maxUsedIndex + 1);
  const fallbackSlot = slots[0];
  return Array.from({ length: count }, (_, idx) =>
    buildExportMaterial((slots[idx] ?? fallbackSlot).material),
  );
}

/**
 * Build a Three.js MeshPhysicalMaterial from the SceneMaterial definition.
 */
function buildExportMaterial(mat: SceneMaterial): THREE.MeshPhysicalMaterial {
  const m = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(mat.color),
    roughness: mat.roughness,
    metalness: mat.metalness,
  });
  if (mat.emissiveColor && mat.emissiveColor !== "#000000") {
    m.emissive = new THREE.Color(mat.emissiveColor);
    m.emissiveIntensity = mat.emissiveIntensity ?? 0;
  }
  if (mat.opacity !== undefined && mat.opacity < 1) {
    m.transparent = true;
    m.opacity = mat.opacity;
  }
  if (mat.clearcoat !== undefined && mat.clearcoat > 0) {
    m.clearcoat = mat.clearcoat;
    m.clearcoatRoughness = mat.clearcoatRoughness ?? 0;
  }
  if (mat.sheen !== undefined && mat.sheen > 0) {
    m.sheen = mat.sheen;
    m.sheenRoughness = mat.sheenRoughness ?? 1;
    if (mat.sheenColor) m.sheenColor = new THREE.Color(mat.sheenColor);
  }
  if (mat.transmission !== undefined && mat.transmission > 0) {
    m.transmission = mat.transmission;
    m.ior = mat.ior ?? 1.5;
    m.thickness = mat.thickness ?? 0;
  }
  if (mat.specularIntensity !== undefined) {
    m.specularIntensity = mat.specularIntensity;
  }
  return m;
}

/**
 * Bake morph target influences into the position attribute.
 * morphTargetsRelative means each morph attribute stores deltas from the base.
 */
function bakeMorphTargets(
  geo: THREE.BufferGeometry,
  influences: number[],
): void {
  const morphAttrs = geo.morphAttributes.position as
    | THREE.BufferAttribute[]
    | undefined;
  if (!morphAttrs || morphAttrs.length === 0) return;
  if (!influences.some((w) => w !== 0)) return;

  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const positions = posAttr.array as Float32Array;

  for (let m = 0; m < morphAttrs.length; m++) {
    const w = influences[m];
    if (!w) continue;
    const morphArr = morphAttrs[m].array as Float32Array;
    for (let i = 0; i < positions.length; i++) {
      positions[i] += morphArr[i] * w;
    }
  }

  posAttr.needsUpdate = true;
  // Also bake morph normals if present
  const morphNormals = geo.morphAttributes.normal as
    | THREE.BufferAttribute[]
    | undefined;
  if (morphNormals) {
    const normalAttr = geo.getAttribute("normal") as
      | THREE.BufferAttribute
      | undefined;
    if (normalAttr) {
      const normals = normalAttr.array as Float32Array;
      for (let m = 0; m < morphNormals.length; m++) {
        const w = influences[m];
        if (!w) continue;
        const mArr = morphNormals[m].array as Float32Array;
        for (let i = 0; i < normals.length; i++) {
          normals[i] += mArr[i] * w;
        }
      }
      normalAttr.needsUpdate = true;
    }
  }
  // Clear morph data — it's baked into the base now
  delete geo.morphAttributes.position;
  delete geo.morphAttributes.normal;
}

/**
 * Bake skeletal (skinned mesh) deformation into vertex positions.
 * Computes the same vertex transform the GPU would apply, but on the CPU.
 */
function bakeSkinning(
  skinnedMesh: THREE.SkinnedMesh,
  geo: THREE.BufferGeometry,
): void {
  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const normalAttr = geo.getAttribute("normal") as
    | THREE.BufferAttribute
    | undefined;
  const skinIdxAttr = geo.getAttribute("skinIndex") as THREE.BufferAttribute;
  const skinWtAttr = geo.getAttribute("skinWeight") as THREE.BufferAttribute;
  if (!skinIdxAttr || !skinWtAttr) return;

  const skeleton = skinnedMesh.skeleton;
  skeleton.update();

  const bindMatrix = skinnedMesh.bindMatrix;
  const bindMatrixInverse = skinnedMesh.bindMatrixInverse;

  const vertCount = posAttr.count;
  const srcPos = new Float32Array(posAttr.array);
  const dstPos = new Float32Array(vertCount * 3);
  const srcNorm = normalAttr ? new Float32Array(normalAttr.array) : null;
  const dstNorm = normalAttr ? new Float32Array(vertCount * 3) : null;

  const skinIdx = skinIdxAttr.array;
  const skinWt = skinWtAttr.array;
  const itemSize = skinIdxAttr.itemSize; // typically 4

  const boneMatrices = skeleton.boneMatrices;
  if (!boneMatrices) return;
  const boneCount = skeleton.bones.length;

  const _mat = new THREE.Matrix4();
  const _skinned = new THREE.Matrix4();
  const _v = new THREE.Vector4();
  const _vn = new THREE.Vector3();

  // Pre-compute the constant normal matrices outside the hot loop
  const _bindNrmMat = new THREE.Matrix3().getNormalMatrix(bindMatrixInverse);
  const _bindOutNrmMat = new THREE.Matrix3().getNormalMatrix(bindMatrix);
  const _skinnedNrmMat = new THREE.Matrix3();

  for (let i = 0; i < vertCount; i++) {
    // Transform vertex into bind space
    _v.set(srcPos[i * 3], srcPos[i * 3 + 1], srcPos[i * 3 + 2], 1);
    _v.applyMatrix4(bindMatrixInverse);

    // Blend bone matrices
    _skinned.elements.fill(0);
    for (let j = 0; j < itemSize; j++) {
      const boneIdx = Math.round(skinIdx[i * itemSize + j]);
      const weight = skinWt[i * itemSize + j];
      if (weight === 0 || boneIdx < 0 || boneIdx >= boneCount) continue;
      _mat.fromArray(boneMatrices, boneIdx * 16);
      for (let k = 0; k < 16; k++) {
        _skinned.elements[k] += _mat.elements[k] * weight;
      }
    }

    // Apply blended bone matrix, then bind matrix
    _v.applyMatrix4(_skinned);
    _v.applyMatrix4(bindMatrix);

    dstPos[i * 3] = _v.x;
    dstPos[i * 3 + 1] = _v.y;
    dstPos[i * 3 + 2] = _v.z;

    // Transform normal (ignore translation)
    if (srcNorm && dstNorm) {
      _vn.set(srcNorm[i * 3], srcNorm[i * 3 + 1], srcNorm[i * 3 + 2]);
      _vn.applyMatrix3(_bindNrmMat);
      _skinnedNrmMat.getNormalMatrix(_skinned);
      _vn.applyMatrix3(_skinnedNrmMat);
      _vn.applyMatrix3(_bindOutNrmMat);
      _vn.normalize();
      dstNorm[i * 3] = _vn.x;
      dstNorm[i * 3 + 1] = _vn.y;
      dstNorm[i * 3 + 2] = _vn.z;
    }
  }

  geo.setAttribute("position", new THREE.BufferAttribute(dstPos, 3));
  if (dstNorm) {
    geo.setAttribute("normal", new THREE.BufferAttribute(dstNorm, 3));
  }

  // Remove skinning attributes — geometry is now a static mesh
  geo.deleteAttribute("skinIndex");
  geo.deleteAttribute("skinWeight");
}

/**
 * Run the authoritative modifier worker against the SceneObject's stack and
 * return a fresh `BufferGeometry` baked from the result.
 *
 * Returns `null` when the object has no enabled modifiers, no SceneObject
 * record (e.g. mannequin SkinnedMesh wrappers), or when worker evaluation
 * fails — the caller falls back to the rendered mesh's geometry in those
 * cases. Required because the rendered mesh's geometry can lag the modifier
 * stack: `useModifierEvaluation` is async, edit/sculpt modes suppress the
 * `displayedGeometryModifiers` list, and exports must reflect the current
 * stack regardless of viewport state.
 */
async function evaluateExportGeometry(
  state: ExportState,
  objDef: SceneObject | undefined,
): Promise<THREE.BufferGeometry | null> {
  if (!objDef) return null;
  const overrides = state.modifierOverrides.get(objDef.id);
  if (!overrides || overrides.length === 0) return null;
  const merged = mergeModifierStack([], overrides);
  if (!merged.some((m) => m.enabled)) return null;

  try {
    // Match the renderer's owner-transform policy (PrimitiveRenderer.tsx):
    // modifiers that consult sibling scene objects (array offset, shrinkwrap,
    // curve, boolean, instance source) need the owner's full world transform
    // and the dependency map in world space so target geometry maps into
    // owner-local space consistently across collection nesting. Standalone
    // modifiers (subsurf, mirror, bevel, …) take identity so output is
    // invariant under the owner's placement, matching the renderer's cache key.
    const needsWorld = modifierStackNeedsOwnerWorldTransform(merged);
    const owner = needsWorld
      ? (() => {
          const collection = getCollectionForObject(
            state.collections,
            objDef.id,
          );
          const world = worldTransformForSceneObject(objDef, collection);
          return {
            position: world.position,
            rotation: world.rotation,
            scale: world.scale,
            vertexGroups: objDef.vertexGroups,
          };
        })()
      : {
          position: [0, 0, 0] as [number, number, number],
          rotation: [0, 0, 0] as [number, number, number],
          scale: [1, 1, 1] as [number, number, number],
          vertexGroups: objDef.vertexGroups,
        };
    const sceneObjectsByName = needsWorld
      ? buildSceneObjectWorldNameMap(state.sceneObjects, state.collections)
      : buildSceneObjectNameMap(state.sceneObjects);
    const request = buildModifierEvaluationRequest({
      baseCacheKey: sceneObjectGeometryCacheKey(objDef),
      geometry: buildRenderModifierGeometry(objDef),
      owner,
      modifiers: merged,
      sceneObjectsByName,
      modifierOverrides: state.modifierOverrides,
    });
    const evaluated = await evaluateModifierStackAsync(request);
    const autoSmoothAngle = resolveAutoSmoothAngle(
      objDef.shading,
      objDef.autoSmoothAngle,
    );
    return finalizePerCorner(evaluated, autoSmoothAngle);
  } catch (err) {
    console.warn(
      "[ExportMesh] Modifier evaluation failed; falling back to rendered geometry:",
      err,
    );
    return null;
  }
}

/** Prepare a single mesh's geometry for export (clone, bake, transform). */
function prepareGeometry(
  mesh: THREE.Mesh,
  upAxis: UpAxis,
  includeNormals: boolean,
  includeUVs: boolean,
  scale: number,
  sourceGeometry: THREE.BufferGeometry | null,
): THREE.BufferGeometry {
  // When `sourceGeometry` is supplied, it is the authoritative modifier output
  // for this object — use it verbatim instead of the rendered mesh's geometry,
  // which can lag the stack (worker latency, edit/sculpt modifier suspension).
  // Modifier output has its own vertex count (subsurf/mirror/array reshape it),
  // so morph/skin attributes sized for the rendered mesh do not apply and the
  // bakes below are skipped — modifier-driven SceneObjects do not carry shape
  // keys or skeletal binding anyway.
  const usingModifierGeometry = sourceGeometry !== null;
  const geo = sourceGeometry ?? mesh.geometry.clone();

  // Track whether baking changed vertex positions, invalidating original normals.
  let bakedGeometry = false;

  if (!usingModifierGeometry) {
    // The GPU pipeline stashes morphTargetInfluences off the mesh (replacing it
    // with []) so Three.js doesn't CPU-blend morphs.  The real array — including
    // corrective shape key weights — is kept on userData.stashedInfluences.
    // Similarly, morph position attributes are stripped from the subdivided
    // geometry and stashed on userData.stashedMorphPositions.
    const stashedMorphPos = mesh.userData?.stashedMorphPositions as
      | THREE.BufferAttribute[]
      | undefined;
    if (
      stashedMorphPos &&
      stashedMorphPos.length > 0 &&
      !geo.morphAttributes.position
    ) {
      geo.morphAttributes.position = stashedMorphPos;
    }
    const influences =
      (mesh.userData?.stashedInfluences as number[] | undefined) ??
      mesh.morphTargetInfluences;
    if (influences && influences.length > 0) {
      bakeMorphTargets(geo, [...influences]);
      bakedGeometry = true;
    }
    if ((mesh as any).isSkinnedMesh) {
      bakeSkinning(mesh as THREE.SkinnedMesh, geo);
      bakedGeometry = true;
    }
  }

  mesh.updateWorldMatrix(true, false);
  geo.applyMatrix4(mesh.matrixWorld);

  if (scale !== 1) geo.scale(scale, scale, scale);
  if (upAxis === "Y") geo.rotateX(-Math.PI / 2);

  if (!includeNormals) {
    geo.deleteAttribute("normal");
  } else if (bakedGeometry || !geo.getAttribute("normal")) {
    // Morph-target and skinning baking move vertices but leave normals stale
    // (mannequin GLBs typically lack morph normal deltas, so bakeMorphTargets
    // only updates positions). Recompute smooth normals from the final mesh.
    computeWeldedNormals(geo);
  }
  if (!includeUVs) geo.deleteAttribute("uv");

  return geo;
}

async function doExport(
  format: ExportFormat,
  upAxis: UpAxis,
  includeNormals: boolean,
  includeUVs: boolean,
  scale: number,
): Promise<void> {
  const scene = window.__poseref_scene;
  if (!scene) return;

  const state = useStore.getState();
  // If nothing is selected, export every scene object. This lets the
  // hamburger-menu Export entry behave as a whole-scene export.
  const selectedLeafIds = selectionToLeafObjectIds(
    state.blendFiles,
    state.collections,
    state.selectedObjectTargets,
  );
  const candidateSourceKeys: string[] =
    selectedLeafIds.size > 0
      ? Array.from(selectedLeafIds)
      : Array.from(state.sceneObjects.keys());
  const sourceKeys = filterVisibleMeshExportSourceKeys(
    candidateSourceKeys,
    state,
  );

  const entries: {
    key: string;
    mesh: THREE.Mesh;
    objDef: ReturnType<typeof state.sceneObjects.get>;
    modifierGeometry: THREE.BufferGeometry | null;
  }[] = [];
  for (const key of sourceKeys) {
    const mesh = findMeshByStoreKey(scene, key);
    if (!mesh) continue;
    const objDef = state.sceneObjects.get(key);
    const modifierGeometry = await evaluateExportGeometry(state, objDef);
    entries.push({ key, mesh, objDef, modifierGeometry });
  }
  if (entries.length === 0) return;

  const isSingle = entries.length === 1;
  const firstDef = entries[0].objDef;
  const exportName = isSingle
    ? (firstDef?.name ?? entries[0].mesh.name) || "export"
    : "export";
  const sceneObjectsByName = buildSceneObjectNameMap(state.sceneObjects);

  try {
    if (format === "obj") {
      const registry = createOBJMaterialRegistry();
      const objBlocks: string[] = [];
      const offsets = { v: 0, vt: 0, vn: 0 };
      const downloadStem = buildPairedExportStem(exportName);

      for (const { mesh, objDef, modifierGeometry } of entries) {
        const geo = prepareGeometry(
          mesh,
          upAxis,
          includeNormals,
          includeUVs,
          scale,
          modifierGeometry,
        );
        const name = (objDef?.name ?? mesh.name) || "Object";
        const materialSlots = resolveExportMaterialSlots(
          state,
          sceneObjectsByName,
          objDef,
        );
        const materialNamesByIndex = buildOBJMaterialNamesByIndex(
          geo,
          materialSlots,
          registry,
        );

        const block = serializeOBJBlock(geo, name, offsets, {
          topology: objDef?.topology,
          materialNamesByIndex,
        });
        objBlocks.push(block.text);
        offsets.v += block.vCount;
        offsets.vt += block.vtCount;
        offsets.vn += block.vnCount;
      }

      const hasMaterials = registry.entries.length > 0;
      const objFileName = `${downloadStem}.obj`;
      const mtlFileName = hasMaterials ? `${downloadStem}.mtl` : undefined;
      const header = `# PoseRef OBJ Export\n${mtlFileName ? `mtllib ${mtlFileName}\n` : ""}`;

      triggerDownload(
        new Blob([header, objBlocks.join("")], { type: "text/plain" }),
        objFileName,
      );

      if (hasMaterials) {
        const mtlDownloadFileName = `${downloadStem}.mtl`;
        const mtlBlocks: string[] = ["# PoseRef MTL Export\n"];
        for (const entry of registry.entries) {
          mtlBlocks.push(serializeMTL(entry.name, entry.material));
        }
        const mtlText = mtlBlocks.join("\n");
        setTimeout(() => {
          triggerDownload(
            new Blob([mtlText], { type: "text/plain" }),
            mtlDownloadFileName,
          );
        }, 200);
      }
    } else {
      // GLB — add all meshes to a single scene
      const exportScene = new THREE.Scene();
      const materials: THREE.Material[] = [];

      for (const { mesh, objDef, modifierGeometry } of entries) {
        const geo = prepareGeometry(
          mesh,
          upAxis,
          includeNormals,
          includeUVs,
          scale,
          modifierGeometry,
        );
        const name = (objDef?.name ?? mesh.name) || "Object";
        const materialSlots = resolveExportMaterialSlots(
          state,
          sceneObjectsByName,
          objDef,
        );
        const mat =
          buildGLBExportMaterial(geo, materialSlots) ??
          new THREE.MeshStandardMaterial({ color: 0xcccccc });
        const exportMesh = new THREE.Mesh(geo, mat);
        exportMesh.name = name;
        exportScene.add(exportMesh);
        if (Array.isArray(mat)) materials.push(...mat);
        else materials.push(mat);
      }

      const exporter = new GLTFExporter();
      const result = await exporter.parseAsync(exportScene, { binary: true });
      triggerDownload(
        new Blob([result as ArrayBuffer], { type: "application/octet-stream" }),
        `${exportName}.glb`,
      );
      for (const m of materials) m.dispose();
    }
  } catch (err) {
    console.error("[ExportMesh] Export failed:", err);
  }
}

// ---------------------------------------------------------------------------
// Checkbox row
// ---------------------------------------------------------------------------

function CheckboxRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: FONT_SIZE_BODY,
        color: COLOR_TEXT_SECONDARY,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: COLOR_ACCENT }}
      />
      {label}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ExportMeshModalBody({ close }: { close: () => void }) {
  const [format, setFormat] = useState<ExportFormat>("obj");
  const [upAxis, setUpAxis] = useState<UpAxis>("Y");
  const [includeNormals, setIncludeNormals] = useState(true);
  const [includeUVs, setIncludeUVs] = useState(true);
  const [scale, setScale] = useState(1);
  const isTrial = useIsTrial();
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);

  const handleExport = useCallback(async () => {
    await doExport(format, upAxis, includeNormals, includeUVs, scale);
    close();
  }, [format, upAxis, includeNormals, includeUVs, scale, close]);

  const selectStyle: React.CSSProperties = {
    ...PANEL_INPUT_STYLE,
    fontSize: FONT_SIZE_BODY,
    cursor: "pointer",
  };

  return (
    <ModalShell
      open
      onClose={close}
      onConfirm={handleExport}
      width={340}
      style={{ gap: 14 }}
    >
      <span
        style={{
          fontSize: FONT_SIZE_MODAL_TITLE,
          fontWeight: 600,
          color: COLOR_TEXT,
        }}
      >
        {hasSelection ? "Export Selection" : "Export Scene"}
      </span>

      {/* Format */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_SECONDARY,
            width: 70,
          }}
        >
          Format
        </span>
        {isTrial ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ ...selectStyle, display: "block" }}>OBJ (.obj)</span>
            <span style={{ fontSize: FONT_SIZE_TINY, color: COLOR_GOLD }}>
              Subscribe to export glTF Binary (.glb)
            </span>
          </div>
        ) : (
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
            style={selectStyle}
          >
            <option value="obj">OBJ (.obj)</option>
            <option value="glb">glTF Binary (.glb)</option>
          </select>
        )}
      </div>

      {/* Up Axis */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_SECONDARY,
            width: 70,
          }}
        >
          Up Axis
        </span>
        <select
          value={upAxis}
          onChange={(e) => setUpAxis(e.target.value as UpAxis)}
          style={selectStyle}
        >
          <option value="Y">Y Up</option>
          <option value="Z">Z Up</option>
        </select>
      </div>

      {/* Scale */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_SECONDARY,
            width: 70,
          }}
        >
          Scale
        </span>
        <input
          type="number"
          value={scale}
          onChange={(e) => setScale(parseFloat(e.target.value) || 1)}
          min={0.001}
          step={0.1}
          style={{ ...selectStyle, width: 80 }}
        />
      </div>

      {/* Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <CheckboxRow
          label="Include Normals"
          checked={includeNormals}
          onChange={setIncludeNormals}
        />
        <CheckboxRow
          label="Include UVs"
          checked={includeUVs}
          onChange={setIncludeUVs}
        />
      </div>

      {/* Buttons */}
      <ModalActions marginTop={4}>
        <Button onClick={close} variant="secondary">
          Cancel
        </Button>
        <Button onClick={handleExport} variant="primary">
          Export
        </Button>
      </ModalActions>
    </ModalShell>
  );
}
